import { Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import useTankRelationshipState from "../../../components/tank-relationships-view-content/TankRelationshipsViewState";
import SideSheetRadioGroup from "../../../components/side-sheet/side-sheet-radio-group/SideSheetRadioGroup";
import { ActionTypeEnum, StatusEnum } from "../../../common/IExportNotificationMsg";
import { useSummaryListingContext } from "../SummaryListingContext";
import { actionItems } from "../SummaryListingActions";
import TankBatteryTable, { TankBatteryTableType } from "../../../components/tank-relationships-view-content/tank-battery-table-view/TankBatteryTableView";
import { ApplicationMimeTypesEnum } from "../../../common/enums/ApplicationMimeTypesEnum";
import TankRelationshipDataService from "../../../services/tank-relationship-data-service/TankRelationshipDataService";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import UUID from "../../../common/utilities/Uuid";
import { IFilterSettings } from "../../../services/tank-relationship-data-service/Models";
import { RestError } from "../../../services/rest-service/RestService";

export type DesktopTankBatteryViewProps = {
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type DesktopTankBatteryViewType = {
    executeAction: (action: BaseActionEnum) => void;
};

function DesktopTankBatteryView({
    setViewOptionState
}: DesktopTankBatteryViewProps, ref: Ref<unknown> | undefined) {

    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const tankPadTable = useRef<TankBatteryTableType>(undefined);

    const { 
        state: summaryListingState,
        setActions, 
        //setTankBatteryCount 
    } = useSummaryListingContext();

    const {
        state,
        setFilterText,
        setDownloadUid,
        setExpanded,
        resetState
    } = useTankRelationshipState();

    const [progressState, actionStarted] = useProgressContext();    

    useImperativeHandle<unknown, DesktopTankBatteryViewType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    // Don't want to do this. This hides the grid when column filters are applied
    // that result in to data. If the grid is hidden, the user can't see the filters
    // anymore.
    // function onFilterChanged(event: FilterChangedEvent) {
    //     setNoData(event.api.getRenderedNodes().length === 0);
    // }

    function executeAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                tankPadTable.current?.executeAction(action);
                break;

            case BaseActionEnum.PDF:
                onExport(ApplicationMimeTypesEnum.PDF);
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel);
                break;

        }
    }

    async function onExport(mimeType: ApplicationMimeTypesEnum) {
        const uid = UUID.generate();
        setDownloadUid(uid);


        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: 'Tank Battery Export',
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            const filterSettings: IFilterSettings = {

                filterType: state.filterType,
                name: state.filterText,
                capacityFilledOp: state.capacityFilled?.operator ?? '',
                capacityFilled: state.capacityFilled?.value,
                BOPDOp: state.BOPD?.operator ?? '',
                BOPD: state.BOPD?.value,
                BWPDOp: state.BWPD?.operator ?? '',
                BWPD: state.BWPD?.value
            } 

            await TankRelationshipDataService.exportData(uid, routeContext.groupID as number, filterSettings, mimeType);

        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }


    function onResetOptions() {
        resetState();
    }

    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed)) {

            setDownloadUid(undefined);

        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid,
    ]);    
    
    useEffect(() => {

            const items = [...actionItems].filter(o => o.action === BaseActionEnum.Refresh || o.action === BaseActionEnum.Export);

            const exportAction = items.find(o => o.action === BaseActionEnum.Export); 

            if (exportAction) {
                exportAction.disabled = (progress && progress.uid === state.downloadUid) || (summaryListingState.tankBatteryCount === 0);
            }

            // if (!_.isEqual(items, summaryListingState.actions)) {
                setActions(items);
            // }

    }, [
        progress,
        setActions,
        state.downloadUid,
        // summaryListingState.actions,
        summaryListingState.tankBatteryCount
    ]);

    useEffect(() => {
        setViewOptionState?.(state.viewOptionState);
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);
    
    return (

        <ContentContainer>
            <ContentSection>
                <TankBatteryTable 
                    ref={tankPadTable}
                    state={state} 
                />
            </ContentSection>

            <OptionsSideSheet onReset={onResetOptions}>
                <SideSheetFilterInput
                    label="Find in Tank Battery List"
                    value={state.filterText}
                    onChange={(value) => {
                        setFilterText(value);
                    }}
                />

                <SideSheetRadioGroup
                    label={t('tankRelationships.expandCollapse')}
                    value={state.expanded ? 1 : 0}
                    radioButtons={[{
                        label: t('tankRelationships.expandAll'),
                        ariaLabel: "expand all",
                        value: 1
                    }, {
                        label: t('tankRelationships.collapseAll'),
                        ariaLabel: "collapse all",
                        value: 0
                    }]}
                    onChange={(event) => {
                        const value = +event.target.value;
                        setExpanded(value === 1);
                    }} />                                
            </OptionsSideSheet>
        </ContentContainer>
    );
}

export default forwardRef(DesktopTankBatteryView);